import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useOrganization();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for error in URL hash
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const errorParam = hashParams.get("error");
        const errorDescription = hashParams.get("error_description");
        
        if (errorParam) {
          setError(errorDescription || errorParam);
          return;
        }

        // Wait for the session to be established
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          setError(sessionError.message);
          return;
        }
        
        if (session) {
          // Ensure profile exists for all auth methods (Google OAuth, magic link, etc.)
          // Pass organization_id based on current domain context
          try {
            await supabase.rpc('approve_eventbrite_registrant', {
              p_user_id: session.user.id,
              p_email: session.user.email || '',
              p_organization_id: organization?.id || null,
            });
          } catch (rpcError) {
            // Log but don't fail - profile may exist or user may not be from Eventbrite
            console.log("Profile sync (non-critical):", rpcError);
          }
          
          // Get the return URL from localStorage (set before redirect)
          let returnUrl = localStorage.getItem("auth_return_url") || "/dashboard";
          localStorage.removeItem("auth_return_url");
          
          // If no explicit return URL, check for pending team invitations
          if (returnUrl === "/dashboard" && session.user.email) {
            try {
              const { data: pendingInvite } = await supabase
                .from("team_member_invitations")
                .select("token")
                .eq("invitee_email", session.user.email.toLowerCase())
                .eq("status", "pending")
                .gt("expires_at", new Date().toISOString())
                .limit(1)
                .maybeSingle();
              
              if (pendingInvite?.token) {
                returnUrl = `/invite/accept/${pendingInvite.token}`;
              }
            } catch (inviteError) {
              console.log("Pending invite check (non-critical):", inviteError);
            }
          }
          
          toast.success("Logged in successfully!");
          navigate(returnUrl, { replace: true });
        } else {
          // Session might not be ready yet, wait a moment and check again
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          
          if (retrySession) {
            // Ensure profile exists
            // Pass organization_id based on current domain context
            try {
              await supabase.rpc('approve_eventbrite_registrant', {
                p_user_id: retrySession.user.id,
                p_email: retrySession.user.email || '',
                p_organization_id: organization?.id || null,
              });
            } catch (rpcError) {
              console.log("Profile sync retry (non-critical):", rpcError);
            }
            
            let returnUrl = localStorage.getItem("auth_return_url") || "/dashboard";
            localStorage.removeItem("auth_return_url");
            
            // If no explicit return URL, check for pending team invitations
            if (returnUrl === "/dashboard" && retrySession.user.email) {
              try {
                const { data: pendingInvite } = await supabase
                  .from("team_member_invitations")
                  .select("token")
                  .eq("invitee_email", retrySession.user.email.toLowerCase())
                  .eq("status", "pending")
                  .gt("expires_at", new Date().toISOString())
                  .limit(1)
                  .maybeSingle();
                
                if (pendingInvite?.token) {
                  returnUrl = `/invite/accept/${pendingInvite.token}`;
                }
              } catch (inviteError) {
                console.log("Pending invite check retry (non-critical):", inviteError);
              }
            }
            
            toast.success("Logged in successfully!");
            navigate(returnUrl, { replace: true });
          } else {
            setError("Unable to establish session. Please try again.");
          }
        }
      } catch (err: any) {
        setError(err.message || "Authentication failed");
      }
    };

    handleAuthCallback();
  }, [navigate, location, organization]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="text-destructive text-lg font-medium">
            Authentication Error
          </div>
          <p className="text-muted-foreground">{error}</p>
          <button 
            onClick={() => navigate("/login")}
            className="text-primary hover:underline"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
